import { FieldProps } from '@rjsf/utils';
import { useMemo } from 'react'; // Import useMemo
import usePrompt from 'api/prompts/usePrompt.ts';
import { Box, FormHelperText } from '@mui/material';
import { SectionTitle } from './SectionTitle';
import { Form } from '@rjsf/mui';
import { customizeValidator } from '@rjsf/validator-ajv8';
import { JSONSchema7 } from 'json-schema';
import { UiSchema } from '@rjsf/utils/src/types.ts';
import { ColumnFromFileWidget } from './ColumnFromFileWidget';
import { NodeColumnsInfo } from 'api/workflows-steps/formSchema';

type PromptInputsFieldOptions = {
  fixedInputs?: string[];
  columns?: NodeColumnsInfo[];
};

const PromptInputsField = (props: FieldProps) => {
  const options = (props.uiSchema?.['ui:options'] ?? {}) as PromptInputsFieldOptions;
  const formData = props.formContext?.formData;
  const promptId = formData?.configuration?.prompt_id;
  const revision = formData?.configuration?.prompt_revision;

  const { prompt } = usePrompt({ promptId, revision });

  const validator = customizeValidator({
    ajvOptionsOverrides: { useDefaults: true, verbose: true },
  });

  const { schema, uiSchema } = useMemo(() => {
    const schema: JSONSchema7 = { type: 'object', properties: {} };
    const uiSchema: UiSchema = {};

    if (!prompt || !prompt.inputs) {
      return { schema, uiSchema };
    }

    const schemaProperties: Record<string, JSONSchema7> = {};

    for (const input of prompt.inputs) {
      if (options.fixedInputs?.includes(input)) {
        continue;
      }

      schemaProperties[input] = {
        type: 'string',
        title: input,
      };

      uiSchema[input] = {
        'ui:widget': ColumnFromFileWidget,
        'ui:options': { columns: options.columns ?? [] },
      };
    }

    schema.properties = schemaProperties;
    schema.required = prompt.inputs.filter((input) => !options.fixedInputs?.includes(input));

    return { schema, uiSchema };
  }, [prompt?.inputs, options.fixedInputs, options.columns]);

  if (!prompt || !promptId) {
    return <FormHelperText>Select a prompt to see input fields</FormHelperText>;
  }

  if (Object.keys(schema.properties ?? {}).length === 0) {
    return <FormHelperText>This prompt has no configurable input fields</FormHelperText>;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <SectionTitle title="Prompt Inputs" />

      <Form
        schema={schema}
        uiSchema={uiSchema}
        formData={props.formData}
        validator={validator}
        onChange={(data) => props.onChange(data.formData ?? {})}
        onSubmit={() => {}}
        omitExtraData={false}
        liveValidate={true}
        showErrorList={false}
        noHtml5Validate={true}
        tagName="div"
        formContext={props.formContext}
        disabled={props.disabled}
      >
        <div style={{ display: 'none' }} />
      </Form>
    </Box>
  );
};

export default PromptInputsField;
